import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _set from "lodash/set";

import {
  FIRE_SUPPRESSION_SYSTEMS,
  MEASUREMENT_UNITS,
  PROPERTY_CHARACTERISTICS,
  WAREHOUSE_LIGHTING_TYPE,
} from "../../utils/constants";
import { filterOption } from "../../utils/utils";

const emptyValues = ["", undefined, null];

export const hasSyncValue = value => !emptyValues.includes(value);

const getOptionText = (options, value) => {
  const option = filterOption(options, value);
  return option || value;
};

const formatBoolean = value => {
  if (value === true || value === "1" || `${value}`.toLowerCase() === "yes") return "Yes";
  if (value === false || value === "0" || `${value}`.toLowerCase() === "no") return "No";
  return value;
};

const formatMeasurement = (value, uom) => {
  if (!hasSyncValue(value)) return value;
  return hasSyncValue(uom) ? `${value} ${uom}` : value;
};

export const formatFeetInches = value => {
  if (!hasSyncValue(value)) return value;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  const totalInches = Math.round(numericValue * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${feet} ft ${inches} in`;
};

const getFeetInchesDecimal = (feet, inches) => {
  if (!hasSyncValue(feet) && !hasSyncValue(inches)) return undefined;
  const numericFeet = Number(feet || 0);
  const numericInches = Number(inches || 0);
  if (!Number.isFinite(numericFeet) || !Number.isFinite(numericInches)) return undefined;
  return numericFeet + numericInches / 12;
};

const getClearHeightRange = source => {
  const minimumClearHeight = _get(source, "buildingInfo.minimumClearHeight");
  const maximumClearHeight = _get(source, "buildingInfo.maximumClearHeight");
  const minimum = hasSyncValue(minimumClearHeight)
    ? minimumClearHeight
    : getFeetInchesDecimal(
      _get(source, "buildingInfo.minimumClearHeightFeet"),
      _get(source, "buildingInfo.minimumClearHeightInches"),
    );
  const maximum = hasSyncValue(maximumClearHeight)
    ? maximumClearHeight
    : getFeetInchesDecimal(
      _get(source, "buildingInfo.maximumClearHeightFeet"),
      _get(source, "buildingInfo.maximumClearHeightInches"),
    );
  return { minimum, maximum };
};

const formatFeetInchesRange = value => {
  if (!value || typeof value !== "object") return formatFeetInches(value);
  const minimum = formatFeetInches(value.minimum);
  const maximum = formatFeetInches(value.maximum);
  if (!hasSyncValue(minimum)) return maximum;
  if (!hasSyncValue(maximum) || `${minimum}` === `${maximum}`) return minimum;
  return `${minimum} – ${maximum}`;
};

export const getClearHeightScalarValue = value => {
  if (!value || typeof value !== "object") return value;
  if (hasSyncValue(value.maximum)) return value.maximum;
  return value.minimum;
};

export const getUnitClearHeightRange = source => ({
  minimum: _get(source, "characteristics.minimumClearHeight", _get(source, "minimumClearHeight")),
  maximum: _get(source, "characteristics.maximumClearHeight", _get(source, "maximumClearHeight")),
});

const getPreviewVersionUpdate = row =>
  Object.prototype.hasOwnProperty.call(row, "previewVersion") ? { previewVersion: row.previewVersion } : {};

const getPersistedDpmValue = row =>
  Object.prototype.hasOwnProperty.call(row, "persistedDpmValue") ? row.persistedDpmValue : row.dpmValue;

export const getBuildingSyncUpdates = rows =>
  rows.map(row => ({
    id: row.id,
    path: row.path,
    value: row.updatedValue,
    budValue: row.budValue === undefined ? null : row.budValue,
    dpmValue: getPersistedDpmValue(row) === undefined ? null : getPersistedDpmValue(row),
    ...getPreviewVersionUpdate(row),
  }));

export const getUnitClearHeightUpdates = row => {
  if (row.id !== "clearHeight" || !row.updatedValue || typeof row.updatedValue !== "object") {
    return [
      {
        id: row.id,
        path: row.path,
        value: row.updatedValue === null ? undefined : row.updatedValue,
        budValue: row.budValue === undefined ? null : row.budValue,
        dpmValue: getPersistedDpmValue(row) === undefined ? null : getPersistedDpmValue(row),
        ...getPreviewVersionUpdate(row),
      },
    ];
  }

  const { minimum, maximum } = row.updatedValue;
  const budValue = row.budValue && typeof row.budValue === "object" ? row.budValue : {};
  const currentValue =
    [row.persistedDpmValue, row.dpmClearHeightRange, row.dpmValue].find(value => value && typeof value === "object") ||
    {};
  const persistedScalarValue = Object.prototype.hasOwnProperty.call(row, "persistedDpmScalarValue")
    ? row.persistedDpmScalarValue
    : getClearHeightScalarValue(getPersistedDpmValue(row));
  return [
    {
      id: "minimumClearHeight",
      path: "minimumClearHeight",
      value: minimum,
      budValue: budValue.minimum === undefined ? null : budValue.minimum,
      dpmValue: currentValue.minimum === undefined ? null : currentValue.minimum,
      ...getPreviewVersionUpdate(row),
    },
    {
      id: "maximumClearHeight",
      path: "maximumClearHeight",
      value: maximum,
      budValue: budValue.maximum === undefined ? null : budValue.maximum,
      dpmValue: currentValue.maximum === undefined ? null : currentValue.maximum,
      ...getPreviewVersionUpdate(row),
    },
    {
      id: "clearHeight",
      path: "clearHeight",
      value: getClearHeightScalarValue(row.updatedValue),
      budValue: getClearHeightScalarValue(row.budValue) === undefined ? null : getClearHeightScalarValue(row.budValue),
      dpmValue: persistedScalarValue === undefined ? null : persistedScalarValue,
      ...getPreviewVersionUpdate(row),
    },
  ];
};

const normalizeClearHeightValue = value => {
  if (!hasSyncValue(value)) return "";
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return `${Number(numericValue.toFixed(6))}`;
  return `${value}`.trim().toLowerCase();
};

const clearHeightRangesAreEqual = (left, right) => {
  const leftRange = left && typeof left === "object" ? left : { minimum: left, maximum: left };
  const rightRange = right && typeof right === "object" ? right : { minimum: right, maximum: right };
  return (
    normalizeClearHeightValue(leftRange.minimum) === normalizeClearHeightValue(rightRange.minimum) &&
    normalizeClearHeightValue(leftRange.maximum) === normalizeClearHeightValue(rightRange.maximum)
  );
};

export const normalizeUnitClearHeightPreviewRows = rows =>
  (rows || []).map(row =>
    row.id === "clearHeight"
      ? {
        ...row,
        isEqual: clearHeightRangesAreEqual(row.budValue, row.dpmValue),
      }
      : row,
  );

export const unitContainsCode = (unit, unitCode) =>
  unit?.unitId === unitCode ||
  (Array.isArray(unit?.unitCode) &&
    unit.unitCode.some(code => (code && typeof code === "object" ? code.value : code) === unitCode));

const feetInchesCharacteristicKeys = [
  "truckCourtDepth",
  "speedBayDepth",
  "typicalColumnSpacing1",
  "typicalColumnSpacing2",
  "buildingDepth",
  "buildingLength",
  "backSpeedBayDepthMeasurement",
  "backTruckCourtDepthMeasurement",
];

const getPropertyCharacteristicFormat = key => {
  if (key === "railServed" || key === "crossDock") return formatBoolean;
  if (feetInchesCharacteristicKeys.includes(key)) return formatFeetInches;
  return undefined;
};

export const formatDisplayValue = (value, formatter, fallback = "-", source) => {
  const displayValue = formatter ? formatter(value, source) : value;
  if (displayValue && typeof displayValue === "object") {
    const objectDisplayValue = [displayValue.label, displayValue.name, displayValue.value].find(hasSyncValue);
    return hasSyncValue(objectDisplayValue) ? objectDisplayValue : JSON.stringify(displayValue);
  }
  return hasSyncValue(displayValue) ? displayValue : fallback;
};

export const buildSyncRows = ({ fields, budSource, dpmSource }) =>
  fields.map(field => {
    const budValue = field.getBudValue ? field.getBudValue(budSource) : _get(budSource, field.budPath || field.key);
    const dpmValue = field.getDpmValue ? field.getDpmValue(dpmSource) : _get(dpmSource, field.dpmPath || field.key);
    const path = Object.prototype.hasOwnProperty.call(field, "path") ? field.path : field.key;
    const applyValue = field.getApplyValue ? { applyValue: field.getApplyValue(budValue, budSource) } : {};
    return {
      id: field.id || field.key,
      fieldName: field.label,
      path,
      formPath: Object.prototype.hasOwnProperty.call(field, "formPath") ? field.formPath : path,
      canApply: path !== undefined,
      budValue,
      ...applyValue,
      dpmValue,
      budDisplayValue: formatDisplayValue(budValue, field.formatBud || field.format, "-", budSource),
      dpmDisplayValue: formatDisplayValue(dpmValue, field.formatDpm || field.format, "-", dpmSource),
    };
  });

const buildRefreshedDisplaySource = (rows, fields, valueKey, pathKey) =>
  rows.reduce((source, row) => {
    const field = fields.find(candidate => (candidate.id || candidate.key) === row.id);
    if (!field) return source;
    const path = field[pathKey] || field.key;
    _set(source, path, row[valueKey]);
    return source;
  }, {});

export const formatRefreshedSyncRows = (rows = [], fields = []) => {
  const budSource = buildRefreshedDisplaySource(rows, fields, "budValue", "budPath");
  const rowsWithLocalDpmValues = rows.map(row => ({
    ...row,
    dpmValue: Object.prototype.hasOwnProperty.call(row, "localDpmValue") ? row.localDpmValue : row.dpmValue,
  }));
  const dpmSource = buildRefreshedDisplaySource(rowsWithLocalDpmValues, fields, "dpmValue", "dpmPath");
  return rowsWithLocalDpmValues.map(row => {
    const field = fields.find(candidate => (candidate.id || candidate.key) === row.id);
    if (!field) return row;
    return {
      ...row,
      budDisplayValue: formatDisplayValue(row.budValue, field.formatBud || field.format, "-", budSource),
      dpmDisplayValue: formatDisplayValue(row.dpmValue, field.formatDpm || field.format, "-", dpmSource),
    };
  });
};

export const buildCharacteristicsRows = ({ fields, source }) =>
  fields
    .filter(field => field.showInCharacteristics !== false)
    .map(field => {
      const value = field.getDpmValue ? field.getDpmValue(source) : _get(source, field.dpmPath || field.key);
      return {
        key: field.id || field.key,
        label: field.label,
        value: formatDisplayValue(value, field.formatDpm || field.format, "-", source),
      };
    });

export const getBuildingSyncFields = (t, featureFlag = {}) => {
  const fields = [
    {
      key: "propertyCode",
      label: t("BuildingInfo.BuildingCode"),
      budPath: "propertyInfo.propertyCode",
      dpmPath: "buildingInfo.propertyCode",
      path: "buildingInfo.propertyCode",
    },
    {
      key: "propertyRollUp",
      label: t("Table.PropertyRollUpCode"),
      getBudValue: source => _get(source, "buildingInfo.propertyRollUp", _get(source, "propertyInfo.propertyRollUp")),
      dpmPath: "buildingInfo.propertyRollUp",
      path: "buildingInfo.propertyRollUp",
    },
    {
      key: "marketId",
      label: t("Property.Market"),
      budPath: "market.id",
      dpmPath: "buildingInfo.marketId",
      path: "market",
      formPath: "buildingInfo.marketId",
      getBudValue: source => _get(source, "market.id", _get(source, "market")),
      getDpmValue: source =>
        _get(source, "buildingInfo.marketId.id", _get(source, "buildingInfo.marketId", _get(source, "market.id"))),
      format: value => _get(value, "id", value),
    },
    {
      key: "streetAddress1",
      label: t("Property.StreetAddress1"),
      budPath: "address.streetAddress1",
      dpmPath: "buildingInfo.address.streetAddress1",
      path: "buildingInfo.address.streetAddress1",
      showInCharacteristics: false,
    },
    {
      key: "streetAddress2",
      label: t("Property.StreetAddress2"),
      budPath: "address.streetAddress2",
      dpmPath: "buildingInfo.address.streetAddress2",
      path: "buildingInfo.address.streetAddress2",
      showInCharacteristics: false,
    },
    {
      key: "city",
      label: t("Property.City"),
      budPath: "address.city",
      dpmPath: "buildingInfo.address.city",
      path: "buildingInfo.address.city",
      showInCharacteristics: false,
    },
    {
      key: "state",
      label: t("Property.State"),
      budPath: "address.state",
      dpmPath: "buildingInfo.address.state",
      path: "buildingInfo.address.state",
      showInCharacteristics: false,
    },
    {
      key: "postalCode",
      label: t("Property.PostalCode"),
      getBudValue: source => _get(source, "address.zipCode", _get(source, "address.postalCode")),
      dpmPath: "buildingInfo.address.postalCode",
      path: "buildingInfo.address.postalCode",
      showInCharacteristics: false,
    },
    {
      key: "propertySubtype",
      label: t("BuildingInfo.PropertyType"),
      getBudValue: source => _get(source, "propertyInfo.propertySubtype", _get(source, "propertyInfo.propertyType")),
      dpmPath: "buildingInfo.propertySubtype",
      path: "buildingInfo.propertySubtype",
    },
    {
      key: "marketingPropertyName",
      label: t("Property.MarketingPropertyName"),
      budPath: "propertyInfo.marketingPropertyName",
      dpmPath: "buildingInfo.marketingPropertyName",
      path: "buildingInfo.marketingPropertyName",
    },
    {
      key: "totalBuildingSize",
      label: t("BuildingInfo.TotalBuildingSize"),
      budPath: "propertyInfo.totalBuildingSize",
      dpmPath: "buildingInfo.totalBuildingSize",
      path: "buildingInfo.totalBuildingSize",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "propertyInfo.totalBuildingSizeUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "buildingInfo.totalBuildingSizeUOM")),
    },
    {
      key: "totalBuildingSizeUOM",
      label: t("BuildingInfo.BuildingUOM"),
      budPath: "propertyInfo.totalBuildingSizeUOM",
      dpmPath: "buildingInfo.totalBuildingSizeUOM",
      path: "buildingInfo.totalBuildingSizeUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "minSpaceAvailable",
      label: t("BuildingInfo.MinSpaceAvailable"),
      budPath: "propertyInfo.minSpaceAvailable",
      dpmPath: "buildingInfo.minSpaceAvailable",
      path: "buildingInfo.minSpaceAvailable",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "propertyInfo.minSpaceAvailableUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "buildingInfo.minSpaceAvailableUOM")),
    },
    {
      key: "minSpaceAvailableUOM",
      label: t("BuildingInfo.MinUOM"),
      budPath: "propertyInfo.minSpaceAvailableUOM",
      dpmPath: "buildingInfo.minSpaceAvailableUOM",
      path: "buildingInfo.minSpaceAvailableUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "maxSpaceAvailable",
      label: t("BuildingInfo.MaxSpaceAvailable"),
      budPath: "propertyInfo.maxSpaceAvailable",
      dpmPath: "buildingInfo.maxSpaceAvailable",
      path: "buildingInfo.maxSpaceAvailable",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "propertyInfo.maxSpaceAvailableUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "buildingInfo.maxSpaceAvailableUOM")),
    },
    {
      key: "maxSpaceAvailableUOM",
      label: t("BuildingInfo.MaxUOM"),
      budPath: "propertyInfo.maxSpaceAvailableUOM",
      dpmPath: "buildingInfo.maxSpaceAvailableUOM",
      path: "buildingInfo.maxSpaceAvailableUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "railServed",
      label: t("BuildingInfo.RailServed"),
      budPath: "characteristics.railServed",
      dpmPath: "buildingInfo.railServed",
      path: "buildingInfo.railServed",
      format: formatBoolean,
    },
    {
      key: "truckCourtDepth",
      label: t("BuildingInfo.TruckCourtDepth"),
      budPath: "characteristics.truckCourtDepth",
      dpmPath: "buildingInfo.truckCourtDepth",
      path: "buildingInfo.truckCourtDepth",
      format: formatFeetInches,
    },
    {
      key: "clearHeight",
      label: t("UnitCharacteristics.ClearHeight"),
      path: "clearHeight",
      formPath: null,
      getBudValue: source => ({
        minimum: _get(source, "characteristics.minimumClearHeight"),
        maximum: _get(source, "characteristics.maximumClearHeight"),
      }),
      getDpmValue: getClearHeightRange,
      format: formatFeetInchesRange,
    },
    {
      key: "availableDate",
      label: t("Property.AvailableDate"),
      getBudValue: source => _get(source, "buildingInfo.availableDate", _get(source, "effectiveFromDate")),
      dpmPath: "buildingInfo.availableDate",
      path: "buildingInfo.availableDate",
    },
    {
      key: "digitalTourUrl",
      label: t("Property.DigitalTourURL"),
      budPath: "buildingInfo.digitalTourUrl",
      dpmPath: "buildingInfo.digitalTourUrl",
      path: "buildingInfo.digitalTourUrl",
    },
    {
      key: "videoUrl",
      label: t("Property.VideoURL"),
      budPath: "buildingInfo.videoUrl",
      dpmPath: "buildingInfo.videoUrl",
      path: "buildingInfo.videoUrl",
    },
    {
      key: "micrositeUrl",
      label: t("Property.MicrositeURL"),
      budPath: "buildingInfo.micrositeUrl",
      dpmPath: "buildingInfo.micrositeUrl",
      path: "buildingInfo.micrositeUrl",
    },
    {
      key: "latitude",
      label: t("Property.Latitude"),
      budPath: "latitude",
      dpmPath: "buildingInfo.latitude",
      path: "latitude",
      formPath: "buildingInfo.latitude",
      getDpmValue: source => _get(source, "buildingInfo.latitude", _get(source, "latitude")),
    },
    {
      key: "longitude",
      label: t("Property.Longitude"),
      budPath: "longitude",
      dpmPath: "buildingInfo.longitude",
      path: "longitude",
      formPath: "buildingInfo.longitude",
      getDpmValue: source => _get(source, "buildingInfo.longitude", _get(source, "longitude")),
    },
  ];

  if (featureFlag.SUBMARKET?.status) {
    fields.push({
      key: "subMarketId",
      label: t("Property.Submarket"),
      budPath: "subMarket",
      dpmPath: "buildingInfo.subMarketId",
      path: "subMarket",
      formPath: "buildingInfo.subMarketId",
      getBudValue: source => _get(source, "subMarket.id", _get(source, "subMarket")),
      getDpmValue: source =>
        _get(
          source,
          "buildingInfo.subMarketId.id",
          _get(source, "buildingInfo.subMarketId", _get(source, "subMarket.id", _get(source, "subMarket"))),
        ),
      format: value => _get(value, "id", value),
    });
  }

  return [
    ...fields,
    ...PROPERTY_CHARACTERISTICS.filter(characteristic => !fields.some(field => field.key === characteristic.value)).map(
      characteristic => ({
        key: characteristic.value,
        label: characteristic.label,
        budPath: `characteristics.${characteristic.value}`,
        dpmPath: `buildingInfo.${characteristic.value}`,
        path: `buildingInfo.${characteristic.value}`,
        format: getPropertyCharacteristicFormat(characteristic.value),
      }),
    ),
  ];
};

export const getUnitSyncFields = (t, featureFlag = {}) =>
  [
    {
      key: "unitCode",
      label: t("UnitInfoForm.UnitCode"),
      budPath: "unitCode",
      dpmPath: "unitCode",
      getDpmValue: source => {
        const unitCode = _get(source, "unitCode");
        if (Array.isArray(unitCode)) return unitCode.map(item => item.label || item.value).join(", ");
        return unitCode;
      },
    },
    {
      key: "marketingUnitName",
      label: t("UnitInfoForm.MarketingUnitName"),
      budPath: "marketingUnitName",
      dpmPath: "marketingUnitName",
    },
    {
      key: "minSpaceAvailable",
      label: t("BuildingInfo.MinSpaceAvailable"),
      budPath: "minSpaceAvailable",
      dpmPath: "minSpaceAvailable",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "minSpaceAvailableUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "minSpaceAvailableUOM")),
    },
    {
      key: "minSpaceAvailableUOM",
      label: t("UnitInfoForm.MinSpaceAvailableUOM"),
      budPath: "minSpaceAvailableUOM",
      dpmPath: "minSpaceAvailableUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "maxSpaceAvailable",
      label: t("BuildingInfo.MaxSpaceAvailable"),
      budPath: "maxSpaceAvailable",
      dpmPath: "maxSpaceAvailable",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "maxSpaceAvailableUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "maxSpaceAvailableUOM")),
    },
    {
      key: "maxSpaceAvailableUOM",
      label: t("UnitInfoForm.MaxSpaceAvailableUOM"),
      budPath: "maxSpaceAvailableUOM",
      dpmPath: "maxSpaceAvailableUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "officeSize",
      label: t("UnitInfoForm.OfficeSize"),
      budPath: "officeSize",
      dpmPath: "officeSize",
      formatBud: (value, source) => formatMeasurement(value, _get(source, "officeSizeUOM")),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "officeSizeUOM")),
    },
    {
      key: "feature1",
      label: t("KeyFeatures.KeyFeature1"),
      budPath: "keyFeatures.feature1",
      dpmPath: "keyFeatures.feature1",
      path: "keyFeatures.feature1",
    },
    {
      key: "feature2",
      label: t("KeyFeatures.KeyFeature2"),
      budPath: "keyFeatures.feature2",
      dpmPath: "keyFeatures.feature2",
      path: "keyFeatures.feature2",
    },
    {
      key: "feature3",
      label: t("KeyFeatures.KeyFeature3"),
      budPath: "keyFeatures.feature3",
      dpmPath: "keyFeatures.feature3",
      path: "keyFeatures.feature3",
    },
    {
      key: "feature4",
      label: t("KeyFeatures.KeyFeature4"),
      budPath: "keyFeatures.feature4",
      dpmPath: "keyFeatures.feature4",
      path: "keyFeatures.feature4",
    },
    {
      key: "officeSizeUOM",
      label: t("UnitInfoForm.UOMLabel"),
      budPath: "officeSizeUOM",
      dpmPath: "officeSizeUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "mezzanineSpace",
      label: t("UnitInfoForm.MezzanineSpace"),
      getBudValue: source =>
        _get(source, "mezzanineSpace", _get(source, "maximumMezzanineArea", _get(source, "minimumMezzanineArea"))),
      dpmPath: "mezzanineSpace",
      formatBud: (value, source) =>
        formatMeasurement(
          value,
          _get(
            source,
            "mezzanineSpaceUOM",
            _get(source, "maximumMezzanineAreaUOM", _get(source, "minimumMezzanineAreaUOM")),
          ),
        ),
      formatDpm: (value, source) => formatMeasurement(value, _get(source, "mezzanineSpaceUOM")),
    },
    {
      key: "mezzanineSpaceUOM",
      label: t("UnitInfoForm.UOMLabel"),
      getBudValue: source =>
        _get(
          source,
          "mezzanineSpaceUOM",
          _get(source, "maximumMezzanineAreaUOM", _get(source, "minimumMezzanineAreaUOM")),
        ),
      dpmPath: "mezzanineSpaceUOM",
      format: value => getOptionText(MEASUREMENT_UNITS, value),
    },
    {
      key: "warehouseLightingType",
      label: t("UnitCharacteristics.WarehouseLightingType"),
      budPath: "characteristics.warehouseLightingType",
      dpmPath: "warehouseLightingType",
      format: value => getOptionText(WAREHOUSE_LIGHTING_TYPE, value),
    },
    {
      key: "clearHeight",
      label: t("UnitCharacteristics.ClearHeight"),
      getBudValue: featureFlag.property_search_redesign?.status
        ? getUnitClearHeightRange
        : source => _get(source, "characteristics.clearHeight", _get(source, "clearHeight")),
      getDpmValue: featureFlag.property_search_redesign?.status
        ? source => {
          const range = getUnitClearHeightRange(source);
          const scalar = _get(source, "characteristics.clearHeight", _get(source, "clearHeight"));
          return {
            minimum: hasSyncValue(range.minimum) ? range.minimum : scalar,
            maximum: hasSyncValue(range.maximum) ? range.maximum : scalar,
          };
        }
        : source => _get(source, "characteristics.clearHeight", _get(source, "clearHeight")),
      format: formatFeetInchesRange,
    },
    {
      key: "clearHeightUOM",
      label: t("UnitInfoForm.ClearHeightUOM"),
      budPath: "characteristics.clearHeightUOM",
      dpmPath: "clearHeightUOM",
    },
    {
      key: "mainBreakerSize",
      label: t("UnitCharacteristics.MainBreakerSizeInAmps"),
      budPath: "characteristics.mainBreakerSize",
      dpmPath: "mainBreakerSize",
    },
    {
      key: "fireSuppressionSystem",
      label: t("UnitCharacteristics.FireSuppressionSystem"),
      budPath: "characteristics.fireSuppressionSystem",
      dpmPath: "fireSuppressionSystem",
      format: value => getOptionText(FIRE_SUPPRESSION_SYSTEMS, value),
    },
    {
      key: "gradeLevelDoors",
      label: t("UnitCharacteristics.GradeLevelDoors"),
      budPath: "characteristics.gradeLevelDoors",
      dpmPath: "gradeLevelDoors",
    },
    {
      key: "dockHighDoors",
      label: t("UnitCharacteristics.DockHighDoors"),
      budPath: "characteristics.dockHighDoors",
      dpmPath: "dockHighDoors",
    },
    {
      key: "hvacWarehouse",
      label: t("UnitCharacteristics.HVACEvaporativeCooling"),
      budPath: "characteristics.hvacWarehouse",
      dpmPath: "hvacWarehouse",
    },
    {
      key: "crossDock",
      label: t("UnitCharacteristics.UnitCrossDock"),
      budPath: "characteristics.crossDock",
      dpmPath: "characteristics.crossDock",
      formPath: "characteristics.crossDock",
      format: formatBoolean,
    },
    {
      key: "officeLightingType",
      label: t("UnitCharacteristics.OfficeLightingType"),
      budPath: "characteristics.officeLightingType",
      dpmPath: "officeLightingType",
    },
    {
      key: "edgeOfDockLevelersCount",
      label: t("PropertyCharacteristics.EdgeOfDockLevelersCount"),
      budPath: "characteristics.edgeOfDockLevelersCount",
      dpmPath: "edgeOfDockLevelersCount",
    },
    {
      key: "pitLevelersCount",
      label: t("UnitCharacteristics.PitLevelersCount"),
      budPath: "characteristics.pitLevelersCount",
      dpmPath: "pitLevelersCount",
    },
    {
      key: "vehicleRestraintsCount",
      label: t("UnitCharacteristics.VehicleRestraintsCount"),
      budPath: "characteristics.vehicleRestraintsCount",
      dpmPath: "characteristics.vehicleRestraintsCount",
      formPath: "characteristics.vehicleRestraintsCount",
    },
  ].filter(field => !_isEmpty(field.label));
